import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { RichText, toPlainText } from "../../../components/RichText";

interface HatInfoProps {
  displayName: string;
  description?: unknown;
  demandPercentChange?: number;
  /** Pre-computed demand label from region data (e.g. "High Demand") */
  demandLabel?: string;
  /** Region name appended to demand label (e.g. "Texas") */
  demandRegionName?: string;
}

const TitleSection = styled(Box, {
  label: "HatInfoTitleSection",
})(() => ({
  position: "relative",
  height: 64,
  width: "100%",
  textAlign: "center",
}));

const HatName = styled(Typography, {
  label: "HatName",
})(() => ({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 16,
  fontFamily: "Nunito",
  fontWeight: 700,
  fontSize: "32px",
  lineHeight: "42px",
  color: "#ffffff",
  textAlign: "center",
}));

const DemandLabel = styled(Typography, {
  label: "HatDemandLabel",
})(() => ({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "16px",
  color: "#ffd666",
  textAlign: "center",
  height: 16,
}));

const ContentStack = styled(Box, {
  label: "HatInfoContentStack",
})(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  width: "100%",
}));

const DescriptionText = styled(Box, {
  label: "HatDescription",
})(() => ({
  fontFamily: "Proxima Nova",
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  overflow: "hidden",
  textOverflow: "ellipsis",
  width: "100%",
}));

const getDemandLabelFromPercent = (percentChange?: number): string | null => {
  if (percentChange == null) return null;
  if (percentChange >= 10) return "High Demand";
  if (percentChange >= 3) return "Growing";
  return null;
};

export const HatInfo = ({
  displayName,
  description,
  demandPercentChange,
  demandLabel: demandLabelProp,
  demandRegionName,
}: HatInfoProps) => {
  const demandLabel =
    demandLabelProp || getDemandLabelFromPercent(demandPercentChange);

  const demandText = demandLabel
    ? demandRegionName
      ? `${demandLabel} in ${demandRegionName}`
      : demandLabel
    : null;

  // Serialize rich text to JSON string if it's an object/array
  const richTextValue =
    typeof description === "string"
      ? description
      : description
        ? JSON.stringify(description)
        : null;

  const hasDescription = richTextValue && toPlainText(richTextValue);

  return (
    <>
      <TitleSection>
        <HatName>{displayName}</HatName>
        {demandText && <DemandLabel>{demandText}</DemandLabel>}
      </TitleSection>

      <ContentStack>
        {hasDescription && (
          <DescriptionText>
            <RichText value={richTextValue} />
          </DescriptionText>
        )}
      </ContentStack>
    </>
  );
};
